import clsx from "clsx";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import WButton from "../wbutton/WButton";
import parse from "html-react-parser";
import Image from "next/image";
import { useCallback } from "react";
/* eslint-disable-next-line */
export interface LImageCardsSectionProps {
  id?: string;
  title?: string;
  backgroundColor?: string;
  cards?: {
    large?: boolean;
    image?: {
      url: string;
      altTag: string;
      mime: string;
    };
    logo?: {
      url: string;
      altTag: string;
    };
    text?: string;
    title?: string;
    textColor?: string;
    backgroundColor?: string;
    button?: {
      text: string;
      url: string;
    };
  }[];
}

export function LImageCardsSection(props: LImageCardsSectionProps) {
  const videoCallbackRef = useCallback((el: HTMLVideoElement) => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!window.matchMedia("(hover: hover)").matches) {
          if (entry.isIntersecting) {
            el.play();
          } else {
            el.pause();
            el.currentTime = 0;
          }
        }
      },
      { threshold: 1 }
    );
    if (el) {
      observer.observe(el);
    } else {
      observer.disconnect();
    }
  }, []);

  return (
    <div
      className={props?.backgroundColor ? "" : "bg-eklight"}
      id={props?.id}
      style={{ backgroundColor: props?.backgroundColor }}
    >
      <div className="container flex flex-col gap-10 px-4 py-5 mx-auto lg:max-w-7xl">
        {props?.title && (
          <div className="w-full text-center">
            <h1>{parse(props?.title)}</h1>
          </div>
        )}
        <div className="flex flex-wrap gap-y-6">
          {props?.cards?.map((card, index) => (
            <div
              key={index}
              className={clsx([
                card?.large ? "w-full lg:w-1/3" : "w-full lg:w-1/4",
              ])}
            >
              <div
                className={clsx([
                  "flex flex-col h-full m-3 overflow-hidden rounded-lg shadow-md",
                  card?.backgroundColor ? "" : "bg-white",
                  card?.textColor ? "" : "text-ekdark",
                ])}
                style={{
                  backgroundColor: card?.backgroundColor || "",
                  color: card?.textColor || "",
                }}
              >
                {card?.image?.url && card?.image?.mime?.startsWith("image") && (
                  <Image
                    src={card?.image?.url}
                    alt={card?.image?.altTag || "image"}
                    className="object-cover max-h-[230px] cursor-pointer hover:scale-105 transition-all"
                    onClick={() =>
                      card?.button?.url &&
                      window?.open(card?.button?.url, "_blank")
                    }
                    width={392}
                    height={230}
                    quality={75}
                  />
                )}
                {card?.image?.url && card?.image?.mime?.startsWith("video") && (
                  <video
                    src={card?.image?.url}
                    className="object-cover max-h-[230px] cursor-pointer hover:scale-105 transition-all"
                    onClick={() =>
                      card?.button?.url &&
                      window?.open(card?.button?.url, "_blank")
                    }
                    muted
                    loop
                    playsInline
                    onMouseOver={(e) => {
                      if (window.matchMedia("(hover: hover)").matches) {
                        e.currentTarget.play();
                      }
                    }}
                    onMouseOut={(e) => {
                      if (window.matchMedia("(hover: hover)").matches) {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      }
                    }}
                    ref={videoCallbackRef}
                  />
                )}

                <div className="flex flex-col w-full h-full gap-2 p-5 pt-0 mt-5">
                  {card?.logo?.url && (
                    <div>
                      <Image
                        src={card?.logo?.url}
                        alt={card?.logo?.altTag || "image"}
                        className="max-h-[35px] mx-auto cursor-pointer"
                        onClick={() =>
                          card?.button?.url &&
                          window?.open(card?.button?.url, "_blank")
                        }
                        width={140}
                        height={35}
                        quality={80}
                      />
                    </div>
                  )}

                  {card?.title && (
                    <div>
                      <span
                        className={clsx([
                          card?.large ? "text-xl" : "text-base",
                          "font-bold cursor-pointer hover:text-ekgreen",
                        ])}
                        onClick={() =>
                          card?.button?.url &&
                          window?.open(card?.button?.url, "_blank")
                        }
                      >
                        {card?.title}
                      </span>
                    </div>
                  )}

                  {card?.text && (
                    <div className="small-markdown">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p className="!text-base">{children}</p>
                          ),
                        }}
                      >
                        {card?.text}
                      </ReactMarkdown>
                    </div>
                  )}

                  {card?.button?.text && (
                    <div className="w-full mt-auto mt-full align-self-end">
                      <WButton
                        type="secondary-small"
                        className="mx-auto"
                        text={card?.button?.text}
                        onClick={() =>
                          card?.button?.url &&
                          window?.open(card?.button?.url, "_blank")
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LImageCardsSection;
